import React, {
  useMemo,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import moment from 'moment';
import format from 'string-template';

import useToolsNavigation from '../../../../hooks/useToolsNavigation';
import MealPlanContext, { withMealPlanContextReady } from '../../../../context/MealPlanContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import { getClientPagePath } from '../../../../../utils/routes';
import ChatButton from '../../../ChatButton';
import ClientQuickInfoButton from '../../../ClientQuickInfoButton';
import colors from '../../../../../styles/colors';
import { getUserStatus } from '../../../../utils/userStatus';
import { filterChecks, ClientStatus } from '../../../../utils/statusFilter';
import { DateFormat } from '../../../../../utils/date';
import { ErrorTag } from '../../../../../components/Tags';
import Subscription from '../../../../../Model/Subscription';
import UserContract from '../../../../../Model/UserContract';
import { buildBillingTargets, listedTargets } from '../../../../utils/billingTarget';
import useComponentMounted from '../../../../../hooks/useComponentMounted';

import ProfileDetailsSection from './components/ProfileDetailsSection';
import ClientTagsSection from './components/ClientTagsSection';
import CheckInTemplateSection from './components/CheckInTemplateSection';
import SubscriptionDetailsSection from './components/SubscriptionDetailsSection';
import ProductDetailsSection from './components/ProductDetailsSection';
import CommunitiesSection from './components/CommunitiesSection';
import BASSection from './components/BASSection';
import { accordionId } from './utils';
import {
  Container,
  StyledLink,
  StyledAvatar,
  ButtonContainer,
  StyledButton,
  StyledMealPlanIcon,
  StyledMobileIcon,
  Name,
  StatusSection,
  StatusText,
  StatusValue,
} from './styles';
import texts from './texts.json';
import subscriptionTexts from './components/SubscriptionDetailsSection/texts.json';

const EMPTY_ACCORDION = '';
const EMPTY_CELL = '-';

const UserInfoSection = ({
  clientId,
  userDoc,
  showMobileViewModal,
  isQAP,
}) => {
  const {
    name,
    avatarUrl,
    customTags,
    communityIds,
    canceledAt,
  } = userDoc;

  const {
    userDoc: loggedInUserDoc,
  } = useContext(LoggedInUserContext);

  const [expanded, setExpanded] = useState(accordionId.PROFILE_DETAILS_ACCORDION);
  const [subscriptionsCollection, setSubscriptionsCollection] = useState(null);
  const [contractsCollection, setContractsCollection] = useState(null);

  const isComponentMountedRef = useComponentMounted();

  const { navigateToClientMealPlanPage } = useToolsNavigation();
  const { mealPlanAssignments } = useContext(MealPlanContext);
  const { externalCoachDoc } = useContext(ExternalCoachContext);
  const { id: coachId, stripeAccountId: pageStripeAccountId } = externalCoachDoc;

  useEffect(() => {
    const init = async () => {
      /*
        The same narrowing the billing accordion applies. Unnarrowed, the two panels on this
        screen answer differently: the cancellation tag below takes the soonest date across
        every target, so a coach is told a product they do not sell is ending.
      */
      const [subscriptions, contracts] = await Promise.all([
        Subscription.getCurrentSubscriptionsCollectionForUser(clientId),
        UserContract.getContractsByUserAndCoach(clientId, coachId),
      ]);
      if (isComponentMountedRef.current) {
        setSubscriptionsCollection(subscriptions);
        setContractsCollection(contracts);
      }
    };
    init();
  }, [clientId, coachId, pageStripeAccountId, isComponentMountedRef]);

  /*
    Computed in the render body rather than memoised: `userDoc` keeps one identity for the life
    of the screen and `Collection.docs` only changes on membership, so neither dep moves when a
    contract entry does. The component is an `observer`, so reading them here tracks them.
  */
  const billingTargets = buildBillingTargets({
    userDoc,
    contractDocs: contractsCollection?.docs || [],
    subscriptionDocs: (subscriptionsCollection?.docs || [])
      .filter((doc) => !doc.stripeAccountId || doc.stripeAccountId === pageStripeAccountId),
  });

  // Same set the subscription rows list, so the two accordions never disagree about
  // which products the client holds.
  const productIds = [...new Set(
    listedTargets(billingTargets, userDoc.contractView?.contractId).map(({ productId }) => productId).filter(Boolean),
  )];

  const isMealPlanAvailable = useMemo(() => (
    !!mealPlanAssignments?.find((assignment) => assignment.userId === clientId)
  ), [
    clientId,
    mealPlanAssignments,
  ]);

  // The soonest scheduled cancellation across the client's products.
  const futureCancelLabel = useMemo(() => computed(() => {
    const upcoming = billingTargets
      .filter(({ clientView }) => filterChecks[ClientStatus.FUTURE_CANCELLATION](clientView))
      .map(({ clientView }) => clientView.cancelAt);
    if (!upcoming.length || !canceledAt) {
      return null;
    }
    return format(texts.cancels, { date: moment.unix(Math.min(...upcoming)).format(DateFormat.MONTH_DAY_FORMAT) });
  }), [billingTargets, canceledAt]).get();

  const clientStatus = useMemo(() => computed(() => {
    // A paused client should keep showing "On a break" even when a cancellation is scheduled.
    // The pending cancellation is still surfaced separately via the futureCancelLabel tag below,
    // so only fall back to the "Active" label when the client is not currently on a break.
    if (futureCancelLabel && !filterChecks[ClientStatus.ON_BREAK](userDoc)) {
      return subscriptionTexts.active;
    }
    const { status, associatedDateString } = getUserStatus(userDoc, DateFormat.DEFAULT_DATE_FORMAT);
    let statusText = status;
    if (associatedDateString) {
      statusText += ` (${associatedDateString})`;
    }
    return statusText;
  }), [
    userDoc,
    futureCancelLabel,
  ]).get();

  // Allow only one accordion to be open at a time
  const handleExpandSection = useCallback((accordion) => (event, isExpanded) => {
    setExpanded(isExpanded ? accordion : EMPTY_ACCORDION);
  }, []);

  return (
    <Container id="quickAccessPanel" isQAP={isQAP}>
      <StyledAvatar src={avatarUrl} />
      {isQAP ? (
        <StyledLink to={getClientPagePath(coachId, clientId)}>
          {name}
        </StyledLink>
      ) : (
        <Name>{name}</Name>
      )}
      <StatusSection>
        <StatusText>
          {subscriptionTexts.clientStatus}
          :
          {' '}
          <StatusValue statusColor={
            (clientStatus === subscriptionTexts.active || clientStatus.includes(subscriptionTexts.amendingTerms))
              ? colors.shades.success6 : colors.shades.danger3
            }
          >
            {clientStatus || EMPTY_CELL}
          </StatusValue>
        </StatusText>
        {futureCancelLabel && (
          <ErrorTag>{futureCancelLabel}</ErrorTag>
        )}
      </StatusSection>
      {loggedInUserDoc.isCoachAssistant && (
        <BASSection
          clientId={clientId}
          name={name}
          loggedInUserDoc={loggedInUserDoc}
        />
      )}
      <ButtonContainer>
        <ChatButton
          userId={userDoc.id}
          text={texts.chatLabel}
          popperPlacement="right-start"
          textIconButton
        />
        {!isQAP && <ClientQuickInfoButton userId={userDoc.id} textIconButton />}
        {isMealPlanAvailable && (
          <StyledButton
            onClick={() => navigateToClientMealPlanPage(clientId)}
          >
            <StyledMealPlanIcon />
            {texts.mealPlan}
          </StyledButton>
        )}
        <StyledButton
          onClick={() => showMobileViewModal(true)}
        >
          <StyledMobileIcon />
          {texts.AppView}
        </StyledButton>
      </ButtonContainer>
      <ProfileDetailsSection
        clientId={clientId}
        userDoc={userDoc}
        handleExpand={handleExpandSection}
        expanded={expanded}
        isQAP={isQAP}
      />
      <ClientTagsSection
        userId={clientId}
        customTags={customTags}
        handleExpand={handleExpandSection}
        expanded={expanded}
        isQAP={isQAP}
      />
      <CheckInTemplateSection
        userDoc={userDoc}
        handleExpand={handleExpandSection}
        expanded={expanded}
        isQAP={isQAP}
      />
      <CommunitiesSection
        userDoc={userDoc}
        communityIds={communityIds}
        handleExpand={handleExpandSection}
        expanded={expanded}
        isQAP={isQAP}
      />
      <SubscriptionDetailsSection
        userDoc={userDoc}
        billingTargets={billingTargets}
        handleExpand={handleExpandSection}
        expanded={expanded}
        isQAP={isQAP}
      />
      <ProductDetailsSection
        productIds={productIds}
        handleExpand={handleExpandSection}
        expanded={expanded}
        isQAP={isQAP}
      />
    </Container>
  );
};

UserInfoSection.propTypes = {
  clientId: PropTypes.string.isRequired,
  userDoc: PropTypes.object.isRequired,
  showMobileViewModal: PropTypes.func.isRequired,
  isQAP: PropTypes.bool.isRequired,
};

export default compose(
  withMealPlanContextReady,
  observer,
)(UserInfoSection);
